import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useOptimizedImageUpload } from '@/hooks/useOptimizedImageUpload';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UseBackgroundImageOptions {
  defaultImage: string;
  category: string; // 'calendar', 'tasks', 'persons', 'dashboard', etc.
  familyId?: string; // Optional family ID for clan-specific backgrounds
  fallbackToDefault?: boolean;
}

export function useBackgroundImage({ 
  defaultImage, 
  category, 
  familyId,
  fallbackToDefault = true 
}: UseBackgroundImageOptions) {
  const { user } = useAuth();
  const { uploadImage, uploading } = useOptimizedImageUpload();
  const { toast } = useToast();
  
  const [currentImage, setCurrentImage] = useState(defaultImage);
  const [isLoading, setIsLoading] = useState(false);
  
  console.log('🎨 useBackgroundImage state:', { currentImage, defaultImage, category, familyId, user: user?.id });
  
  // Clé stable basée sur l'utilisateur, la famille et la catégorie
  const storageKey = user ? (familyId ? `bg_${user.id}_${familyId}_${category}` : `bg_${user.id}_${category}`) : null;
  const supabasePath = user ? (familyId ? `backgrounds/${user.id}/${familyId}/${category}` : `backgrounds/${user.id}/${category}`) : null;

  // Récupérer l'image sauvegardée au démarrage
  const loadSavedImage = useCallback(async () => {
    if (!user || !storageKey || !supabasePath) return;

    // Éviter le loading si on a déjà l'image par défaut
    if (currentImage !== defaultImage) {
      setIsLoading(true);
    }
    
    try {
      // 1. Pour le dashboard avec familyId, vérifier d'abord l'image de famille dans la table families
      if (familyId && category === 'dashboard') {
        const { data: familyData } = await supabase
          .from('families')
          .select('background_image_url')
          .eq('id', familyId)
          .single();

        if (familyData?.background_image_url) {
          console.log('🏠 Found family background image:', familyData.background_image_url);
          setCurrentImage(familyData.background_image_url);
          // Synchroniser avec localStorage
          try {
            localStorage.setItem(storageKey, familyData.background_image_url);
          } catch (e) {
            console.warn('Impossible de sauvegarder en localStorage:', e);
          }
          return;
        }
      }

      // 2. Vérifier dans Supabase Storage (source de vérité pour les images utilisateur)
      const searchPath = familyId ? `backgrounds/${user.id}/${familyId}` : `backgrounds/${user.id}`;
      const { data: files } = await supabase.storage
        .from('avatars')
        .list(searchPath, {
          search: category
        });

      if (files && files.length > 0) {
        // Prendre le fichier le plus récent
        const latestFile = files
          .filter(file => file.name.includes(category))
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

        if (latestFile) {
          const filePath = familyId ? `backgrounds/${user.id}/${familyId}/${latestFile.name}` : `backgrounds/${user.id}/${latestFile.name}`;
          const { data: { publicUrl } } = supabase.storage
            .from('avatars')
            .getPublicUrl(filePath);
          
          console.log('🎯 Found Supabase image:', publicUrl);
          setCurrentImage(publicUrl);
          // Synchroniser avec localStorage
          try {
            localStorage.setItem(storageKey, publicUrl);
          } catch (e) {
            console.warn('Impossible de sauvegarder en localStorage:', e);
          }
          return;
        }
      }

      // 3. Fallback: vérifier localStorage
      const localImage = localStorage.getItem(storageKey);
      if (localImage && localImage.startsWith('http')) {
        // Vérifier que l'URL est toujours valide
        try {
          const response = await fetch(localImage, { method: 'HEAD' });
          if (response.ok) {
            setCurrentImage(localImage);
            return;
          }
        } catch {
          // URL invalide, nettoyer localStorage
          localStorage.removeItem(storageKey);
        }
      }

      // 4. Utiliser l'image par défaut
      if (fallbackToDefault) {
        console.log('📸 Using default image:', defaultImage);
        setCurrentImage(defaultImage);
      }

    } catch (error) {
      console.error('Erreur lors du chargement de l\'image de fond:', error);
      if (fallbackToDefault) {
        setCurrentImage(defaultImage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user, storageKey, supabasePath, category, familyId, defaultImage, fallbackToDefault]);

  // Charger l'image sauvegardée au montage
  useEffect(() => {
    loadSavedImage();
  }, [loadSavedImage]);

  // Uploader une nouvelle image
  const updateBackgroundImage = useCallback(async (file: File): Promise<boolean> => {
    if (!user || !storageKey || !supabasePath) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour changer l'image de fond.",
        variant: "destructive",
      });
      return false;
    }

    try {
      const result = await uploadImage(file, {
        bucketPath: supabasePath,
        compressionOptions: {
          maxWidth: 1200,
          maxHeight: 600,
          quality: 0.8
        }
      });

      if (result?.url) {
        setCurrentImage(result.url);
        
        // Sauvegarder dans localStorage
        try {
          localStorage.setItem(storageKey, result.url);
        } catch (e) {
          console.warn('Impossible de sauvegarder en localStorage:', e);
        }

        toast({
          title: "Image mise à jour",
          description: "Votre image de fond a été sauvegardée avec succès.",
        });

        return true;
      }

      return false;

    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'image:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder l'image de fond.",
        variant: "destructive",
      });
      return false;
    }
  }, [user, storageKey, supabasePath, uploadImage, toast]);

  // Réinitialiser à l'image par défaut
  const resetToDefault = useCallback(() => {
    if (!storageKey) return;
    
    setCurrentImage(defaultImage);
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      console.warn('Impossible de nettoyer localStorage:', e);
    }

    toast({
      title: "Image réinitialisée",
      description: "L'image de fond a été remise par défaut.",
    });
  }, [defaultImage, storageKey, toast]);

  return {
    currentImage,
    updateBackgroundImage,
    resetToDefault,
    isLoading: isLoading || uploading,
    isUploading: uploading
  };
}